import React from "react";
import { Clock, Compass, Globe, MapPin, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Package } from "../types";
import { BookingInfo } from "./BookingModal";
import { SHOW_PACKAGE_PRICES } from "../config";

interface Props {
  pkg: Package;
  title: string;
  description: string;
  onBook: (info: BookingInfo) => void;
}

const PackageCard: React.FC<Props> = ({ pkg, title, description, onBook }) => {
  const { t } = useTranslation();
  const isDomestic = pkg.type === "domestic";

  const duration = `${pkg.days} ${t("packages.days")}`;

  const handleBook = () => {
    onBook({
      title,
      subtitle: SHOW_PACKAGE_PRICES
        ? `${duration} · ${t("packages.startingFrom")} ₹${pkg.price.toLocaleString()}`
        : `${duration} · ${t("packages.pricingComingSoon")}`,
      badge: isDomestic ? t("packages.badge.domestic") : t("packages.badge.international"),
      badgeClass: isDomestic
        ? "bg-emerald-100 text-emerald-800"
        : "bg-navy-100 text-navy-800",
    });
  };

  return (
    <div className="group rounded-2xl overflow-hidden bg-white shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1.5 border border-navy-900/5 flex flex-col">
      <div className="relative h-52">
        {pkg.images[0] ? (
          <img
            src={pkg.images[0]}
            alt={title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-navy-800 to-navy-950 flex items-center justify-center">
            <Compass size={48} className="text-brand-400/50" strokeWidth={1.5} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950/70 via-transparent to-transparent" />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full bg-white/95 text-navy-900 backdrop-blur">
          {isDomestic ? <MapPin size={12} className="text-emerald-600" /> : <Globe size={12} className="text-navy-500" />}
          {isDomestic ? t("packages.badge.domestic") : t("packages.badge.international")}
        </span>
        <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-navy-950/70 text-brand-300 backdrop-blur">
          <Clock size={12} />
          {duration}
        </span>
      </div>

      <div className="p-5 flex flex-col flex-grow">
        <h3 className="font-serif text-xl font-bold text-navy-900 mb-1.5">{title}</h3>
        <p className="text-navy-900/55 text-sm leading-relaxed mb-5 flex-grow">{description}</p>
        <div className="flex justify-between items-end gap-3 pt-4 border-t border-navy-900/8">
          {SHOW_PACKAGE_PRICES ? (
            <div>
              <span className="text-[11px] uppercase tracking-wider text-navy-900/40 font-semibold">
                {t("packages.startingFrom")}
              </span>
              <div className="font-serif text-2xl font-bold text-brand-700">
                ₹{pkg.price.toLocaleString()}
              </div>
            </div>
          ) : (
            <div>
              <span className="text-[11px] uppercase tracking-wider text-navy-900/40 font-semibold">
                {t("packages.pricing")}
              </span>
              <div className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-700 mt-1">
                <Sparkles size={14} />
                {t("packages.comingSoon")}
              </div>
            </div>
          )}
          <button
            onClick={handleBook}
            className="bg-gradient-to-r from-brand-600 to-brand-400 text-navy-950 font-bold text-sm px-5 py-2.5 rounded-full shadow-gold hover:-translate-y-0.5 hover:shadow-lg transition-all shrink-0"
          >
            {t("packages.bookButton")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PackageCard;
